import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface UserPremiumStatus {
  userId: string;
  email: string;
  name: string;
  createdAt: string;
  isPremium: boolean;
  premiumSource: 'subscription' | 'override' | null;
  isAdmin: boolean;
  trialDaysRemaining: number;
  trialExpired: boolean;
  productIds: string[];
}

interface UseUserPremiumStatusResult {
  users: UserPremiumStatus[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useUserPremiumStatus(): UseUserPremiumStatusResult {
  const [users, setUsers] = useState<UserPremiumStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserStatuses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all users with their profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('auth_user_id, email, full_name, role, created_at')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch premium products
      const { data: premiumProducts, error: productsError } = await supabase
        .from('premium_products')
        .select('product_id');

      if (productsError) throw productsError;

      const premiumProductIds = new Set(
        premiumProducts?.map(p => p.product_id) || []
      );

      // Fetch all user overrides
      const { data: overrides, error: overridesError } = await supabase
        .from('user_product_overrides')
        .select('user_id, product_id, source');

      if (overridesError) throw overridesError;

      // Fetch all active subscriptions
      const { data: subscriptions, error: subsError } = await supabase
        .from('user_subscriptions')
        .select('user_id, product_id, status')
        .eq('status', 'active');

      if (subsError) throw subsError;

      // Build user status objects
      const userStatuses: UserPremiumStatus[] = (profiles || []).map(profile => {
        const userId = profile.auth_user_id;
        const isAdmin = profile.role === 'admin';

        // Check if user has ANY override (one override grants all premium products)
        const userOverrides = (overrides || []).filter(o => o.user_id === userId);
        const hasOverride = userOverrides.length > 0;

        // Check subscriptions
        const userSubscriptions = (subscriptions || []).filter(s => s.user_id === userId);
        const subscriptionPremiumProducts = userSubscriptions
          .map(s => s.product_id)
          .filter(id => premiumProductIds.has(id));
        const hasSubscription = subscriptionPremiumProducts.length > 0;

        // Determine premium status
        // If user has override, they get ALL premium products
        // If user has subscription, they get only the subscribed products
        const isPremium = hasOverride || hasSubscription;
        const premiumSource = hasOverride ? 'override' : hasSubscription ? 'subscription' : null;
        const productIds = hasOverride 
          ? Array.from(premiumProductIds) // All products if override
          : subscriptionPremiumProducts;   // Only subscribed products otherwise

        // Debug logging for nicole@sleepnest.app
        if (profile.email === 'nicole@sleepnest.app') {
          console.log('Nicole Debug:', {
            userId,
            isAdmin,
            userOverrides: userOverrides.length,
            hasOverride,
            isPremium,
            premiumSource,
            productIds: productIds.length,
            role: profile.role
          });
        }

        // Calculate trial status
        const createdAt = new Date(profile.created_at);
        const now = new Date();
        const daysElapsed = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
        const trialDaysRemaining = Math.max(0, 30 - daysElapsed);
        const trialExpired = daysElapsed >= 30;

        return {
          userId,
          email: profile.email || 'No email',
          name: profile.full_name || 'Unknown',
          createdAt: profile.created_at,
          isPremium,
          premiumSource,
          isAdmin,
          trialDaysRemaining,
          trialExpired,
          productIds
        };
      });

      console.log('Total users fetched:', userStatuses.length);
      console.log('Premium users:', userStatuses.filter(u => u.isPremium && !u.isAdmin).length);

      setUsers(userStatuses);
    } catch (err: any) {
      console.error('Error fetching user premium statuses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserStatuses();
  }, []);

  return {
    users,
    loading,
    error,
    refresh: fetchUserStatuses
  };
}
